import clsx from "clsx";
import styles from "./CompaniesDropdown.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { MagnifyingGlassIcon } from "@/frontend/shared/assets/icons";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { getRouteCompanies, getRouteCompanyProfile } from "@/frontend/shared/const/router";
import { useEffect, useMemo } from "react";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { getQueryParamsFilters } from "@/frontend/components/FilterV2/lib/useDiscoverFilters";

type TFormattedSearchedCompanies = {
  id: number;
  name: string;
  headline: string;
  logoUrl: string;
};

type TCompaniesDropdown = {
  className: string;
  inputValue: string;
  setInputValue: (value: string) => void;
  options: TFormattedSearchedCompanies[];
  isOpen: boolean;
  onClose: () => void;
  isCompaniesLoading?: boolean;
};

export function CompaniesDropdown({
  className,
  inputValue,
  setInputValue,
  options,
  isOpen,
  onClose,
  isCompaniesLoading
}: TCompaniesDropdown) {
  const { navigate } = useHistoricalNavigate();

  function navigateToCompaniesPage() {
    const { query } = getQueryParamsFilters([
      {
        id: "description",
        type: "description",
        value: inputValue,
        isOr: false,
        operand: "is"
      }
    ]);
    navigate(getRouteCompanies() + `?${query.toString()}`, { state: { inputValue } });
    onClose();
    setInputValue("");
  }

  function navigateToCompany({ id }: TFormattedSearchedCompanies) {
    navigate(getRouteCompanyProfile(id));
    onClose();
  }

  useEffect(() => {
    const html = document.documentElement;

    if (isOpen) {
      html.style.overflow = "hidden";
    } else {
      html.style.overflow = "";
    }
    return () => {
      html.style.overflow = "";
    };
  }, [isOpen]);

  const content = useMemo(() => {
    switch (true) {
      case isCompaniesLoading:
        return (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        );
      case options.length > 0:
        return options?.map((company) => (
          <a
            key={company.id}
            className={styles.option}
            href={getRouteCompanyProfile(company.id)}
            target="_blank"
            rel="noreferrer"
          >
            <img src={company.logoUrl} alt={company.name} />
            <div>
              <Typography variant="label-4" color="greyscale800" className={styles.name}>
                {company.name}
              </Typography>
              <Typography variant="body-3" color="greyscale600" className={styles.headline}>
                {company.headline}
              </Typography>
            </div>
          </a>
        ));
      default:
        null;
    }
  }, [inputValue, options, isCompaniesLoading]);

  return (
    <div className={clsx(styles.container, isOpen && styles.isOpen, className)}>
      {content}
      <div
        className={clsx(styles.inputTextContainer, !inputValue && styles.hiddenTextContainer)}
        onClick={navigateToCompaniesPage}
      >
        <div className={styles.inputTextIconContainer}>
          <MagnifyingGlassIcon />
        </div>
        <Typography variant="label-4" color="greyscale800" className={styles.inputText}>
          {inputValue}
        </Typography>
      </div>
    </div>
  );
}
